import Link from 'next/link';
import { CSSProperties } from '@mui/material/styles/createTypography';
import { Box, ThemeProvider } from '@mui/system';
import { Container, Typography } from '@mui/material';
import theme from 'src/style-system/theme';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import Image from 'next/image';

import { reviewCarouselType } from '@utils/types';
import { animationduration } from '@utils/data';
import { AppRoutes } from '@utils/appRoutes';

interface CarouselProps {
  carouselContent: reviewCarouselType[];
  bgColor?: string;
  textColor?: string;
}

const ReviewCarousel = ({
  carouselContent,
  bgColor,
  textColor,
}: CarouselProps) => {
  const indicatorStyles: CSSProperties = {
    background: theme.palette.whitePepper.main,
    width: 10,
    height: 10,
    display: 'inline-block',
    margin: '0 8px',
    borderRadius: '50%',
    border: `1px solid ${theme.palette.cumin.main}`,
    cursor: 'pointer',
  };

  return (
    <>
      <Box bgcolor={bgColor || 'whitePepper.main'}>
        <Container maxWidth="xl" disableGutters>
          <Carousel
            autoPlay
            swipeable={false}
            infiniteLoop
            interval={animationduration}
            showStatus={false}
            showThumbs={false}
            showArrows={false}
            renderIndicator={(onClickHandler, isSelected, index, label) => {
              if (isSelected) {
                return (
                  <li
                    style={{
                      ...indicatorStyles,
                      background: theme.palette.cumin.main,
                    }}
                    aria-label={`Selected: ${label} ${index + 1}`}
                    title={`Selected: ${label} ${index + 1}`}
                  />
                );
              }
              return (
                <li
                  style={indicatorStyles}
                  onClick={onClickHandler}
                  onKeyDown={onClickHandler}
                  value={index}
                  key={index}
                  role="button"
                  tabIndex={0}
                  title={`${label} ${index + 1}`}
                  aria-label={`${label} ${index + 1}`}
                />
              );
            }}
          >
            {carouselContent.map((content) => {
              return (
                <Box key={content.id}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: { xs: '46px 40px 60px', md: '72px 75px' },
                    }}
                  >
                    <Typography
                      variant="h3"
                      align="center"
                      color={textColor || 'cinnamon.main'}
                      sx={{
                        width: { md: '840px', lg: '1085px', xl: '1360px' },
                        fontSize: '54px',
                        lineHeight: '71px',
                      }}
                    >
                      {content.title}
                    </Typography>

                    <Box
                      sx={{
                        margin: { xs: '22px 0 29px' },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        [theme.breakpoints.up('md')]: {
                          flexDirection: 'row',
                          columnGap: '55px',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          position: 'relative',
                          width: { xs: '195px', md: '265px' },
                          height: { xs: '180px', md: '250px' },
                        }}
                      >
                        <Image
                          src={content.icon}
                          alt={content.alt}
                          objectFit="contain"
                          layout="fill"
                          placeholder={content.blurUrl ? 'blur' : 'empty'}
                          blurDataURL={content.blurUrl}
                        />
                      </Box>
                    </Box>
                    <Link href={AppRoutes.REVIEWS}>
                      <Typography
                        variant="caption"
                        align="center"
                        color="garamMasala.main"
                        sx={{
                          textDecoration: 'underline 2px',
                        }}
                      >
                        Read Reviews
                      </Typography>
                    </Link>
                  </Box>
                </Box>
              );
            })}
          </Carousel>
        </Container>
      </Box>
    </>
  );
};

export default ReviewCarousel;
